import React, { useEffect, useState } from 'react';
import { ThumbsUp, Heart, Send, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { getBlogEngagement, reactBlog, addBlogComment, deleteBlogComment } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Textarea } from './ui/textarea';

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

function Avatar({ src, name, size = 32 }) {
  const cls = 'rounded-full flex items-center justify-center font-bold shrink-0';
  if (src) return <img src={src} alt={name} referrerPolicy="no-referrer" className={`${cls} object-cover`} style={{ width: size, height: size }} />;
  return <span className={`${cls} bg-[#2E6BFF]/12 text-[#2E6BFF]`} style={{ width: size, height: size, fontSize: size * 0.4 }}>{name?.charAt(0).toUpperCase()}</span>;
}

export default function BlogEngagement({ slug }) {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { getBlogEngagement(slug).then(setData).catch(() => {}); }, [slug]);

  const reacted = (t) => data?.my_reactions?.includes(t);

  const react = async (t) => {
    if (!user) { toast.error('Sign in to react.'); login(); return; }
    try { setData(await reactBlog(slug, t)); } catch (e) { toast.error('Could not react.'); }
  };

  const submit = async () => {
    if (!user) { toast.error('Sign in to comment.'); login(); return; }
    if (!text.trim()) { toast.error('Write a comment first.'); return; }
    setSending(true);
    try {
      await addBlogComment(slug, text.trim());
      setText('');
      setData(await getBlogEngagement(slug));
      toast.success('Comment posted!');
    } catch (e) {
      if (e?.response?.status === 401) { toast.error('Please sign in.'); login(); }
      else toast.error('Could not post comment.');
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    try { await deleteBlogComment(slug, id); setData(await getBlogEngagement(slug)); }
    catch (e) { toast.error('Could not delete.'); }
  };

  if (!data) return null;

  return (
    <div data-testid="blog-engagement" className="mt-10">
      <div className="flex items-center gap-3 pb-6 border-b border-black/5">
        <button
          data-testid="react-like-btn"
          onClick={() => react('like')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${reacted('like') ? 'bg-[#2E6BFF] text-white' : 'bg-[#F4F4F7] text-[#0E1726] hover:bg-[#e9e9ef]'}`}
        >
          <ThumbsUp size={16} fill={reacted('like') ? 'currentColor' : 'none'} /> {data.reactions.like}
        </button>
        <button
          data-testid="react-heart-btn"
          onClick={() => react('heart')}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${reacted('heart') ? 'bg-[#F43F5E] text-white' : 'bg-[#F4F4F7] text-[#0E1726] hover:bg-[#e9e9ef]'}`}
        >
          <Heart size={16} fill={reacted('heart') ? 'currentColor' : 'none'} /> {data.reactions.heart}
        </button>
        <span className="flex items-center gap-2 text-sm text-[#5B6472] ml-auto">
          <MessageSquare size={16} /> {data.comment_count} {data.comment_count === 1 ? 'comment' : 'comments'}
        </span>
      </div>

      <h3 className="font-display font-bold text-[#0E1726] text-[20px] mt-8 mb-4">Discussion</h3>

      <div className="bg-white rounded-2xl p-6 shadow-[0_20px_50px_-46px_rgba(14,23,38,0.4)]">
        {user ? (
          <>
            <div className="flex items-center gap-2.5 mb-3">
              <Avatar src={user.picture} name={user.name} size={28} />
              <span className="text-sm font-medium text-[#0E1726]">{user.name}</span>
            </div>
            <Textarea data-testid="comment-input" className="min-h-[90px]" placeholder="Share your thoughts on this piece..." value={text} onChange={(e) => setText(e.target.value)} />
            <div>
              <button data-testid="comment-submit-btn" onClick={submit} disabled={sending} className="mt-3 bg-[#0E1B2E] text-white font-semibold px-6 py-2.5 rounded-full inline-flex items-center gap-2 hover:bg-[#1b2a44] transition-colors disabled:opacity-60">
                {sending ? 'Posting...' : 'Post comment'} <Send size={15} />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-3">
            <p className="text-[#5B6472] mb-4">Sign in to like, heart and join the discussion.</p>
            <button data-testid="comment-signin-btn" onClick={login} className="bg-[#2E6BFF] text-white font-semibold px-6 py-2.5 rounded-full">Sign in to comment</button>
          </div>
        )}
      </div>

      <div className="space-y-3 mt-5">
        {data.comments.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="bg-white rounded-2xl p-5 shadow-[0_20px_50px_-48px_rgba(14,23,38,0.4)]">
            <div className="flex items-center gap-3 text-sm">
              <Avatar src={c.author_picture} name={c.author} size={28} />
              <span className="font-semibold text-[#0E1726]">{c.author}</span>
              <span className="text-[#5B6472]">· {timeAgo(c.created_at)}</span>
              {user && c.user_id === user.user_id && (
                <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c.id)} className="ml-auto text-[#5B6472] hover:text-[#F43F5E] transition-colors" title="Delete">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
            <p className="text-[#334155] leading-relaxed mt-2.5 whitespace-pre-wrap">{c.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
